/**
 * sl-wq59.6.4: Passkey (WebAuthn) settings section.
 *
 * List + add + remove passkeys for the current account. Sits alongside
 * TwoFactorSection in the account-setting panel so users can enrol a
 * passwordless login method from the same UI surface as their TOTP setup.
 *
 * Hidden (renders a single row with a polite "unsupported" notice) when
 * the browser has no WebAuthn stack — keeps desktop builds and very old
 * browsers from presenting a non-functional button.
 */
import { ConfirmModal, Input, notify } from '@affine/component';
import {
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { Button } from '@affine/component/ui/button';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { AuthService, isWebAuthnUserCancel } from '@affine/core/modules/cloud';
import type { WebAuthnCredentialSummary } from '@affine/core/modules/cloud/provider/auth';
import { isBackendUnavailableError } from '@affine/core/utils/backend-availability';
import { UserFriendlyError } from '@affine/error';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSettingsOfflineIncompleteLabel } from '../shared/settings-offline-status';

export const PasskeySection = () => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const authService = useService(AuthService);
  const [credentials, setCredentials] = useState<WebAuthnCredentialSummary[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [listUnavailable, setListUnavailable] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [adding, setAdding] = useState(false);
  const [removeTarget, setRemoveTarget] =
    useState<WebAuthnCredentialSummary | null>(null);
  const [removing, setRemoving] = useState(false);

  const supported = useMemo(() => AuthService.isWebAuthnSupported(), []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setListUnavailable(false);
    if (!backendAvailable) {
      setListUnavailable(true);
      setLoading(false);
      return;
    }
    try {
      const list = await authService.listPasskeys();
      setCredentials(list);
    } catch (err) {
      if (isBackendUnavailableError(err)) {
        setListUnavailable(true);
        return;
      }

      notify.error({
        title: UserFriendlyError.fromAny(err).message,
      });
    } finally {
      setLoading(false);
    }
  }, [authService, backendAvailable]);

  useEffect(() => {
    if (supported) {
      void refresh();
    }
  }, [supported, refresh]);

  const onAdd = useAsyncCallback(async () => {
    if (adding) return;
    setAdding(true);
    try {
      await authService.registerPasskey(
        deviceName.trim() ? deviceName.trim() : undefined
      );
      notify.success({
        title: t['com.streamlain.settings.passkeys.added'](),
      });
      setAddOpen(false);
      setDeviceName('');
      await refresh();
    } catch (err) {
      if (isWebAuthnUserCancel(err)) {
        notify.error({
          title: t['com.streamlain.settings.passkeys.add-cancelled'](),
        });
      } else {
        notify.error({
          title: t['com.streamlain.settings.passkeys.add-failed'](),
          message: UserFriendlyError.fromAny(err).message,
        });
      }
    } finally {
      setAdding(false);
    }
  }, [adding, authService, deviceName, refresh, t]);

  const onConfirmRemove = useAsyncCallback(async () => {
    if (!removeTarget || removing) return;
    setRemoving(true);
    try {
      await authService.removePasskey(removeTarget.credentialId);
      notify.success({
        title: t['com.streamlain.settings.passkeys.removed'](),
      });
      setRemoveTarget(null);
      await refresh();
    } catch (err) {
      notify.error({
        title: t['com.streamlain.settings.passkeys.remove-failed'](),
        message: UserFriendlyError.fromAny(err).message,
      });
    } finally {
      setRemoving(false);
    }
  }, [authService, refresh, removeTarget, removing, t]);

  const formatLastUsed = useCallback(
    (iso: string | null) =>
      iso
        ? t['com.streamlain.settings.passkeys.last-used']({
            when: new Date(iso).toLocaleDateString(),
          })
        : t['com.streamlain.settings.passkeys.never-used'](),
    [t]
  );

  if (!supported) {
    return (
      <SettingWrapper title={t['com.streamlain.settings.passkeys']()}>
        <SettingRow
          name={t['com.streamlain.settings.passkeys']()}
          desc={t['com.streamlain.settings.passkeys.unsupported']()}
        >
          <></>
        </SettingRow>
      </SettingWrapper>
    );
  }

  return (
    <>
      <SettingWrapper title={t['com.streamlain.settings.passkeys']()}>
        <SettingRow
          name={t['com.streamlain.settings.passkeys']()}
          desc={t['com.streamlain.settings.passkeys.description']()}
        >
          <Button
            variant="primary"
            onClick={() => setAddOpen(true)}
            disabled={!backendAvailable}
            data-testid="add-passkey-button"
          >
            {t['com.streamlain.settings.passkeys.add']()}
          </Button>
        </SettingRow>

        {listUnavailable && (
          <SettingRow name="" desc={offlineLabel}>
            <></>
          </SettingRow>
        )}

        {!listUnavailable && !loading && credentials.length === 0 && (
          <SettingRow
            name=""
            desc={t['com.streamlain.settings.passkeys.empty']()}
          >
            <></>
          </SettingRow>
        )}

        {credentials.map(c => (
          <SettingRow
            key={c.credentialId}
            name={
              c.deviceName || t['com.streamlain.settings.passkeys.unnamed']()
            }
            desc={formatLastUsed(c.lastUsedAt)}
          >
            <Button
              variant="error"
              onClick={() => setRemoveTarget(c)}
              data-testid={`remove-passkey-${c.credentialId}`}
            >
              {t['com.streamlain.settings.passkeys.remove']()}
            </Button>
          </SettingRow>
        ))}
      </SettingWrapper>

      <ConfirmModal
        open={addOpen}
        onOpenChange={setAddOpen}
        title={t['com.streamlain.settings.passkeys.add']()}
        description={t['com.streamlain.settings.passkeys.adding']()}
        confirmText={t['com.streamlain.settings.passkeys.add']()}
        onConfirm={onAdd}
        confirmButtonOptions={{ loading: adding, variant: 'primary' }}
      >
        <Input
          placeholder={t[
            'com.streamlain.settings.passkeys.device-name-placeholder'
          ]()}
          value={deviceName}
          onChange={setDeviceName}
        />
      </ConfirmModal>

      <ConfirmModal
        open={!!removeTarget}
        onOpenChange={next => {
          if (!next) setRemoveTarget(null);
        }}
        title={t['com.streamlain.settings.passkeys.remove']()}
        description={
          removeTarget?.deviceName ||
          t['com.streamlain.settings.passkeys.unnamed']()
        }
        confirmText={t['com.streamlain.settings.passkeys.remove']()}
        onConfirm={onConfirmRemove}
        confirmButtonOptions={{ loading: removing, variant: 'error' }}
      />
    </>
  );
};
